import { defineStore } from 'pinia'
import { watch } from 'vue'
import { useUserStore } from '@/stores/userStore'
import { db, storage } from '@/firebase/init'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { ref as storageRef, uploadBytes } from 'firebase/storage'
import {
  customerOwnerApi,
  customerOwnerContext,
} from '@/globals/typescript/utils/api/customerOwnerApi'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import { getOrderListBucket, type TOrderListTab } from '@/globals/typescript/utils/orderListBucket'
import {
  Order as RealOrder,
  type IOrder as IRealOrder,
  type IOrderSample,
  type IOrderStatuses,
  type TOrderSampleStatus,
} from '@team-milva/lab-models/resources'
import type { IAddress } from '@team-milva/lab-models'
import type { PhoneNumberMeta } from '@team-milva/utils/phone'

// How long createDraftOrder waits for the new order to reach the live orders list before giving up.
const NEW_ORDER_LIST_WAIT_MS = 5000

export const useOrderStore = defineStore('order', {
  state: () => ({
    // MIL-193 — real-time per-sample registration/analysis status, read straight
    // from the real `Apps/-lab-customer-/Orders/{orderID}` doc (lab-cloud is the
    // only writer, via lab-management's register-samples/analysis-results
    // endpoints — see IOrderSample.status). Keyed by order ID, then by the
    // sample's server-generated code (`IOrderSample.label.id`).
    sampleStatusByOrderID: {} as Record<string, Record<string, TOrderSampleStatus>>,
    unsubscribeSampleStatusByOrderID: {} as Record<string, () => void>,
    // Same real order doc as sampleStatusByOrderID above, just reading the
    // sibling `statuses` field instead of `samples` — one listener, two derived
    // pieces of state, to avoid a second onSnapshot on the same document.
    realOrderStatusesByOrderID: {} as Record<string, IOrderStatuses>,

    // The real, shared Orders — MIL-334 Phase 3. Scoped to the logged-in
    // user only (`customerUserID`), not the whole business account — see the
    // plan's "Decisions Made". Each returned document already carries its own
    // `statuses`/`samples`, so the list doesn't need the per-order
    // subscribeToOrderSampleStatus listener above just to render.
    realOrders: [] as RealOrder[],
    unsubscribeRealOrders: null as (() => void) | null,

    // MIL-334 Phase 4 — shared between CustomerNav.vue's persistent search
    // input (writes it via setSearchQuery) and OrdersListView.vue's filtering
    // (reads it) since the two are sibling components with no other shared
    // state. Not reset in resetStore() — a stale query is harmless (it just
    // starts the next session's list pre-filtered to empty text) and clearing
    // it there would fight the nav component's own v-model on every logout
    // that doesn't also unmount it.
    searchQuery: '',

    // The orders list's selected tab. Lives here rather than in OrdersListView's own data so it
    // survives opening an order or draft and coming back — the view is unmounted in between.
    // Reset on logout (resetStore), unlike searchQuery above.
    ordersListTab: 'active' as TOrderListTab,
  }),

  actions: {
    setSearchQuery(searchQuery: string): void {
      this.searchQuery = searchQuery
    },

    setOrdersListTab(tab: TOrderListTab): void {
      this.ordersListTab = tab
    },

    loadRealOrders(): Promise<RealOrder[]> {
      if (this.realOrdersLoaded) {
        return Promise.resolve(this.realOrders)
      }

      const currentUser = useUserStore().currentUser

      return new Promise((resolve) => {
        if (!currentUser) {
          return resolve([])
        }

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Orders'),
          where('customerUserID', '==', currentUser.id)
        )

        let resolved = false
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const orders: RealOrder[] = []
          querySnapshot.forEach((docSnap) => {
            orders.push(new RealOrder(docSnap.data() as IRealOrder))
          })
          this.realOrders = orders
          this.unsubscribeRealOrders = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(orders)
          }
        })
      })
    },

    subscribeToOrderSampleStatus(orderID: string): void {
      if (this.unsubscribeSampleStatusByOrderID[orderID]) return

      const orderRef = doc(db, 'Apps', '-lab-customer-', 'Orders', orderID)
      const unsubscribe = onSnapshot(orderRef, (docSnap) => {
        const data = docSnap.data() as
          | { samples?: IOrderSample[]; statuses?: IOrderStatuses }
          | undefined
        const statusByCode: Record<string, TOrderSampleStatus> = {}
        for (const sample of data?.samples ?? []) {
          statusByCode[sample.label.id] = sample.status
        }
        this.sampleStatusByOrderID = { ...this.sampleStatusByOrderID, [orderID]: statusByCode }

        if (data?.statuses) {
          this.realOrderStatusesByOrderID = {
            ...this.realOrderStatusesByOrderID,
            [orderID]: data.statuses,
          }
        }
      })

      this.unsubscribeSampleStatusByOrderID = {
        ...this.unsubscribeSampleStatusByOrderID,
        [orderID]: unsubscribe,
      }
    },

    unsubscribeFromOrderSampleStatus(orderID: string): void {
      this.unsubscribeSampleStatusByOrderID[orderID]?.()

      const { [orderID]: _removedUnsubscribe, ...restUnsubscribe } = this.unsubscribeSampleStatusByOrderID
      this.unsubscribeSampleStatusByOrderID = restUnsubscribe

      const { [orderID]: _removedStatus, ...restStatus } = this.sampleStatusByOrderID
      this.sampleStatusByOrderID = restStatus

      const { [orderID]: _removedRealStatuses, ...restRealStatuses } = this.realOrderStatusesByOrderID
      this.realOrderStatusesByOrderID = restRealStatuses
    },

    // MIL-334 Phase 2 — creates a real draft `Order` via lab-cloud, reserving its
    // sample-label prefix in the same transaction. `projectID`, when given,
    // creates the draft already linked to that project (the "Opret ordre" action
    // from inside a project) — see the "Lab Customer — Projects, Floor Plans &
    // Samples" plan.
    // The orders list is a live listener, so a document the API has just created reaches
    // realOrders a moment after the API answers. Resolves once the order is there — straight away
    // when it already is, or when no listener is running (the destination page starts its own and
    // waits for the first snapshot). Gives up after NEW_ORDER_LIST_WAIT_MS rather than hang.
    waitForRealOrder(orderID: string): Promise<void> {
      if (!this.realOrdersLoaded || this.getRealOrderById(orderID)) {
        return Promise.resolve()
      }

      return new Promise((resolve) => {
        const finish = (): void => {
          clearTimeout(timer)
          stopWatching()
          resolve()
        }
        const timer = setTimeout(finish, NEW_ORDER_LIST_WAIT_MS)
        const stopWatching = watch(
          () => this.getRealOrderById(orderID),
          (order) => {
            if (order) finish()
          },
          { flush: 'sync' }
        )
      })
    },

    async createDraftOrder(input: {
      address: { addressLine: string; postalCode: string; city: string }
      caseNumber?: string
      isInsuranceCase?: boolean
      insurancePartnerID?: string
      insuranceNumber?: string
      projectID?: string
    }): Promise<{ orderID: string; labPrefix: string }> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can create an order')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/orders/draft',
        {
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)

      // The draft page treats an order missing from realOrders as a dead link and redirects away.
      await this.waitForRealOrder(result.data.orderID)
      return result.data
    },

    async addSampleToDraft(
      orderID: string,
      input: {
        analysisIDs: string[]
        sampleAreaID: string
        buildingAge?: number
        buildingLocation?: string
        material?: string
        additionalNotes?: string
        floorPlanPin?: { floorPlanID: string; point: { x: number; y: number } } | null
        // Required — already uploaded client-direct to Storage (see
        // uploadLabelPhoto below) before this call, on a path that doesn't
        // need a sampleID, so it always exists by creation time.
        labelPhoto: { path: string; contentType: string; width?: number; height?: number }
        // Required too (min 1, server-enforced) — same trick, via
        // uploadSitePhoto below.
        photos: { path: string; contentType: string; width?: number; height?: number }[]
      }
    ): Promise<{ sampleID: string; sampleCode: string }> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit an order')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/orders/{orderID}/samples',
        {
          path: {
            orderID,
          },
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
      return result.data
    },

    async updateSampleOnDraft(
      orderID: string,
      sampleID: string,
      input: {
        analysisIDs?: string[]
        sampleAreaID?: string
        buildingAge?: number
        buildingLocation?: string
        material?: string
        additionalNotes?: string
        floorPlanPin?: { floorPlanID: string; point: { x: number; y: number } } | null
        // A real object replaces it; null clears it (e.g. deleting the photo
        // meaning to re-take it) — omit entirely to leave it untouched.
        labelPhoto?: { path: string; contentType: string; width?: number; height?: number } | null
        // The sample's site photos as they should be once saved, in order: the ones already
        // there are kept, the ones left out are removed, new ones (uploaded with
        // uploadSitePhoto) are added. At least one stays.
        photos?: { path: string; contentType: string; width?: number; height?: number }[]
      }
    ): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit an order')
      }

      const result = await customerOwnerApi(
        owner,
        'patch',
        '/orders/{orderID}/samples/{sampleID}',
        {
          path: {
            orderID,
            sampleID,
          },
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    /**
     * The customer's surgical edit of a sample once the order is placed: only the four optional text
     * fields, the floor plan pin, the label photo (replaced, never cleared) and the site photos (the
     * final list: added, removed and reordered together). Never the sample area or the analyses — they are not part of the request. An empty string
     * clears a text field, and `null` clears the built year or the pin.
     */
    async updatePlacedSample(
      orderID: string,
      sampleID: string,
      input: {
        buildingAge?: number | null
        buildingLocation?: string
        material?: string
        additionalNotes?: string
        floorPlanPin?: { floorPlanID: string; point: { x: number; y: number } } | null
        labelPhoto?: { path: string; contentType: string; width?: number; height?: number }
        photos?: { path: string; contentType: string; width?: number; height?: number }[]
      }
    ): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit an order')
      }

      const result = await customerOwnerApi(owner, 'patch', '/orders/{orderID}/samples/{sampleID}/details', {
        path: { orderID, sampleID },
        body: input,
      })

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async removeSampleFromDraft(orderID: string, sampleID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit an order')
      }

      const result = await customerOwnerApi(
        owner,
        'delete',
        '/orders/{orderID}/samples/{sampleID}',
        {
          path: {
            orderID,
            sampleID,
          },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    // Never needs a sampleID — the path uses a freshly generated ID (same trick
    // projectStore's own uploadFloorPlanImage uses), so the label-confirmation photo can be
    // uploaded before the sample it belongs to exists yet. See
    // IOrderSample.labelPhoto's own doc comment for why.
    async uploadLabelPhoto(orderID: string, file: File): Promise<{ path: string; contentType: string }> {
      const currentUser = useUserStore().currentUser
      if (!currentUser) {
        throw new Error('Not logged in')
      }

      const id = generateFirestoreId()
      const contentType = file.type || 'image/jpeg'
      const path = `labAccounts/${currentUser.labAccountID}/orders/${orderID}/labelPhotos/${currentUser.id}/${id}`

      await uploadBytes(storageRef(storage, path), file, { contentType })

      return { path, contentType }
    },

    // Same trick as uploadLabelPhoto above, for the sample's site photos
    // ("Billede af prøvested") — a freshly generated ID instead of a sampleID, so a photo can
    // be uploaded before the sample exists, and while editing it before the save that attaches it
    // (the file is only registered on the sample when the customer saves).
    async uploadSitePhoto(orderID: string, file: File): Promise<{ path: string; contentType: string }> {
      const currentUser = useUserStore().currentUser
      if (!currentUser) {
        throw new Error('Not logged in')
      }

      const id = generateFirestoreId()
      const contentType = file.type || 'image/jpeg'
      const path = `labAccounts/${currentUser.labAccountID}/orders/${orderID}/sitePhotos/${currentUser.id}/${id}`

      await uploadBytes(storageRef(storage, path), file, { contentType })

      return { path, contentType }
    },

    // Moves an order to a different project. Locked once the order is invoiced
    // (enforced server-side) — see the "Lab Customer — Projects, Floor Plans &
    // Samples" plan's Decisions Made for why nothing about the order's own
    // inherited fields or its samples' floor-plan pins is re-synced on move.
    async moveOrderToProject(orderID: string, projectID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can move an order')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/orders/{orderID}/project',
        {
          path: {
            orderID,
          },
          body: { projectID },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    // "Lab Customer — Projects, Floor Plans & Samples" plan, Phase 7 — the shared
    // OrderProjectInfoEditPane's onSave for an order (FillOutOrderView.vue/OrderView.vue).
    // Locked server-side once the order is invoiced — see updateOrderInfoService.ts.
    async updateOrderInfo(
      orderID: string,
      input: {
        address?: IAddress
        caseNumber?: string
        isInsuranceCase?: boolean
        insurancePartnerID?: string
        insuranceNumber?: string
      }
    ): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit an order')
      }

      const result = await customerOwnerApi(
        owner,
        'patch',
        '/orders/{orderID}',
        {
          path: {
            orderID,
          },
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    // MIL-417 — "SMS me when a delivery is ready", per order. The number belongs to the ORDER
    // only: the user's own phone (which also receives their SMS login codes) is never changed,
    // server-side either. The on/off choice is also remembered as the user's default for their
    // next order. `realOrders` picks the change up through its own listener — no local patch.
    async updateOrderSmsSettings(
      orderID: string,
      input: { isSmsUpdatesEnabled: boolean; phone?: PhoneNumberMeta }
    ): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit an order')
      }

      const result = await customerOwnerApi(
        owner,
        'patch',
        '/orders/{orderID}/sms-settings',
        {
          path: {
            orderID,
          },
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async removeOrderFromProject(orderID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can move an order')
      }

      const result = await customerOwnerApi(
        owner,
        'delete',
        '/orders/{orderID}/project',
        {
          path: {
            orderID,
          },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    // Archives an order out of the customer's own list — customer-list state only, the lab never
    // sees it (see IOrder.activeState). Allowed for any order; an archived draft is how a draft is
    // removed. realOrders picks the change up via its own onSnapshot listener, same as placeOrder.
    async archiveOrder(orderID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can archive an order')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/orders/{orderID}/archive',
        {
          path: {
            orderID,
          },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async restoreOrder(orderID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can restore an order')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/orders/{orderID}/restore',
        {
          path: {
            orderID,
          },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    // "Lab Customer — Place Order" plan, Phase 5 — the final submit that turns
    // a draft into a real, placed order: assigns TATs, hand-over method/date,
    // computes pricing, assigns the real orderNumber, flips status off
    // 'draft'. realOrders picks up the result via its own onSnapshot listener
    // (loadRealOrders) — no need to patch state here.
    async placeOrder(
      orderID: string,
      input: {
        sampleTatAssignments: { sampleID: string; tatID: string }[]
        handOverMethod: 'drop-point' | 'self-send'
        dropPointID?: string
        date: string
      }
    ): Promise<{ orderID: string; orderNumber: string }> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can place an order')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/orders/{orderID}/place',
        {
          path: {
            orderID,
          },
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
      return result.data
    },

    resetStore(): void {
      Object.values(this.unsubscribeSampleStatusByOrderID).forEach((unsubscribe) => unsubscribe())
      this.unsubscribeSampleStatusByOrderID = {}
      this.sampleStatusByOrderID = {}
      this.realOrderStatusesByOrderID = {}

      if (this.unsubscribeRealOrders) {
        this.unsubscribeRealOrders()
        this.unsubscribeRealOrders = null
      }
      this.realOrders = []
      this.ordersListTab = 'active'
    },
  },

  getters: {
    realOrdersLoaded: (state): boolean => !!state.unsubscribeRealOrders,

    sortedRealOrders: (state): RealOrder[] =>
      [...state.realOrders].sort((a, b) => b.timestamps.createdAt - a.timestamps.createdAt),

    // Every order the customer can still see anywhere: all of them except archived drafts, which
    // are how a draft is removed (see getOrderListBucket). The orders list tabs and the project
    // detail page read this instead of sortedRealOrders.
    visibleRealOrders(): RealOrder[] {
      return this.sortedRealOrders.filter((x) => getOrderListBucket(x) !== 'removed')
    },

    draftOrders(): RealOrder[] {
      return this.sortedRealOrders.filter((x) => getOrderListBucket(x) === 'drafts')
    },

    activeOrders(): RealOrder[] {
      return this.sortedRealOrders.filter((x) => getOrderListBucket(x) === 'active')
    },

    archivedOrders(): RealOrder[] {
      return this.sortedRealOrders.filter((x) => getOrderListBucket(x) === 'archived')
    },

    // MIL-338 — the real order detail page (OrderView.vue and its Results/
    // Report tabs) reads this.
    getRealOrderById:
      (state) =>
      (orderID: string): RealOrder | undefined =>
        state.realOrders.find((order) => order.id === orderID),

    getSampleStatus:
      (state) =>
      (orderID: string, sampleCode: string): TOrderSampleStatus | null =>
        state.sampleStatusByOrderID[orderID]?.[sampleCode] ?? null,

    getRealOrderStatuses:
      (state) =>
      (orderID: string): IOrderStatuses | null =>
        state.realOrderStatusesByOrderID[orderID] ?? null,
  },
})
